<script setup>
const DAYS = [
  ['mon', 'Пн'],
  ['tue', 'Вт'],
  ['wed', 'Ср'],
  ['thu', 'Чт'],
  ['fri', 'Пт'],
  ['sat', 'Сб'],
  ['sun', 'Вс'],
];

defineProps({
  edit: { type: Object, required: true },
  brands: { type: Array, default: () => [] },
  cities: { type: Array, default: () => [] },
  geocoding: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
});

defineEmits(['save', 'geocode', 'add-brand', 'add-city']);

const addInterval = day => day.intervals.push({ from: '09:00', to: '18:00' });
const removeInterval = (day, i) => {
  day.intervals.splice(i, 1);
  if (!day.intervals.length) day.intervals.push({ from: '09:00', to: '18:00' });
};
</script>

<template>
  <div>
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">Бренд</span>
        <div class="flex gap-1">
          <select
            v-model="edit.brand_id"
            class="flex-1 px-2 py-1.5 text-sm border rounded-lg border-n-weak bg-n-solid-1"
          >
            <option :value="null">— не выбран —</option>
            <option v-for="b in brands" :key="b.id" :value="b.id">
              {{ b.name }}
            </option>
          </select>
          <button
            type="button"
            class="px-2 text-sm border rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
            @click="$emit('add-brand')"
          >
            +
          </button>
        </div>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">Город</span>
        <div class="flex gap-1">
          <select
            v-model="edit.city_id"
            class="flex-1 px-2 py-1.5 text-sm border rounded-lg border-n-weak bg-n-solid-1"
          >
            <option :value="null">— не выбран —</option>
            <option v-for="c in cities" :key="c.id" :value="c.id">
              {{ c.name }}
            </option>
          </select>
          <button
            type="button"
            class="px-2 text-sm border rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
            @click="$emit('add-city')"
          >
            +
          </button>
        </div>
      </label>
      <label class="flex flex-col gap-1 sm:col-span-2">
        <span class="text-xs text-n-slate-11">Адрес</span>
        <div class="flex gap-1">
          <input
            v-model="edit.address"
            type="text"
            placeholder="Город, улица, дом"
            class="flex-1 px-2 py-1.5 text-sm border rounded-lg border-n-weak bg-n-solid-1"
          />
          <button
            type="button"
            class="px-3 text-sm border rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-1 whitespace-nowrap"
            :disabled="geocoding"
            @click="$emit('geocode')"
          >
            {{ geocoding ? '…' : 'Координаты по адресу' }}
          </button>
        </div>
        <span class="text-xs text-n-slate-10">
          Координаты:
          {{
            edit.latitude && edit.longitude
              ? `${edit.latitude}, ${edit.longitude}`
              : 'не заданы'
          }}
        </span>
      </label>
    </div>

    <div class="mt-4">
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-medium text-n-slate-12">Время работы</span>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="edit.schedule.always_open" type="checkbox" />
          Круглосуточно
        </label>
      </div>
      <div v-if="!edit.schedule.always_open" class="flex flex-col gap-1.5">
        <div
          v-for="[key, title] in DAYS"
          :key="key"
          class="flex flex-wrap items-center gap-2 py-1"
        >
          <label
            class="flex items-center gap-1.5 w-20 text-sm text-n-slate-11 shrink-0"
          >
            <input v-model="edit.schedule.days[key].open" type="checkbox" />
            {{ title }}
          </label>
          <template v-if="edit.schedule.days[key].open">
            <div
              v-for="(iv, i) in edit.schedule.days[key].intervals"
              :key="i"
              class="flex items-center gap-1"
            >
              <input
                v-model="iv.from"
                type="time"
                class="px-1.5 py-1 text-sm border rounded-md border-n-weak bg-n-solid-1"
              />
              <span class="text-n-slate-10">–</span>
              <input
                v-model="iv.to"
                type="time"
                class="px-1.5 py-1 text-sm border rounded-md border-n-weak bg-n-solid-1"
              />
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-ruby-9"
                @click="removeInterval(edit.schedule.days[key], i)"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
            <button
              type="button"
              class="text-xs text-n-blue-text hover:underline"
              @click="addInterval(edit.schedule.days[key])"
            >
              + интервал
            </button>
          </template>
          <span v-else class="text-sm text-n-slate-10">выходной</span>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between mt-4">
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="edit.is_active" type="checkbox" />
        Филиал активен
      </label>
      <button
        type="button"
        class="px-4 py-1.5 text-sm font-medium text-white rounded-lg bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
        :disabled="saving"
        @click="$emit('save')"
      >
        {{ saving ? 'Сохранение…' : 'Сохранить' }}
      </button>
    </div>
  </div>
</template>
